import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from 'config/api';
import {
  Grid,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  Box,
  TextField,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { ConfigCard, BlueButton } from "styles";
import { RedButton } from "styles";

// Session storage anahtarları
const USER_DATA_KEY = "userMeData";

const AccountSettings = () => {
  const navigate = useNavigate();
  const logoutAndRedirect = () => {
    try {
      // Clear localStorage
      window.localStorage.removeItem("authToken");
      window.localStorage.removeItem("userData");
      window.localStorage.removeItem("bannerSettings");
      window.localStorage.removeItem("userCookies");
      window.localStorage.removeItem("userMeData");
      window.localStorage.removeItem("userCookieSummary");
      // Clear sessionStorage
      window.sessionStorage.removeItem("authToken");
      window.sessionStorage.removeItem("userData");
      window.sessionStorage.removeItem("bannerSettings");
      window.sessionStorage.removeItem("userCookies");
      window.sessionStorage.removeItem("userMeData");
      window.sessionStorage.removeItem("userCookieSummary");
    } catch (e) {
      // ignore
    } finally {
      navigate("/sign-in");
    }
  };
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phoneNumber: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Password change states
  const [pwForm, setPwForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwSuccess, setPwSuccess] = useState("");
  const [pwError, setPwError] = useState("");

  // Delete account dialog & feedback
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleteInfoOpen, setDeleteInfoOpen] = useState(false);

  // Password visibility toggles
  const [showOldPw, setShowOldPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const handleDeleteClick = () => {
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleteLoading(true);
      setDeleteError("");
      const token = sessionStorage.getItem("authToken");
      const res = await fetch(`${API_BASE_URL}/me/delete-account/request`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || data.error || "Hesap silme isteği başarısız.");
      }
      setDeleteInfoOpen(true);
      setDeleteOpen(false);
    } catch (err) {
      setDeleteError(err.message || "Bir hata oluştu.");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Password live validation criteria
  const pwCriteria = {
    length: pwForm.newPassword.length >= 8 && pwForm.newPassword.length <= 16,
    upper: /[A-Z]/.test(pwForm.newPassword),
    lower: /[a-z]/.test(pwForm.newPassword),
    digit: /[0-9]/.test(pwForm.newPassword),
    special: /[^A-Za-z0-9]/.test(pwForm.newPassword),
  };
  const hasInput = pwForm.newPassword.length > 0;

  // Session storage'dan kullanıcı verisini al
  const getCachedUserData = () => {
    const cachedData = sessionStorage.getItem(USER_DATA_KEY);
    return cachedData ? JSON.parse(cachedData) : null;
  };

  // Session storage'a kullanıcı verisini kaydet
  const cacheUserData = (userData) => {
    sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
  };

  // Kullanıcı verisini güncelle
  const updateUserData = (newData) => {
    const updatedUser = {
      ...user,
      user: {
        ...user.user,
        ...newData
      }
    };
    setUser(updatedUser);
    cacheUserData(updatedUser);
    setForm({
      name: updatedUser.user.name || "",
      email: updatedUser.user.email || "",
      phoneNumber: updatedUser.user.phoneNumber || "",
    });
  };

  useEffect(() => {
    // Önce cache'den kontrol et
    const cachedUser = getCachedUserData();
    if (cachedUser) {
      setUser(cachedUser);
      setForm({
        name: cachedUser.user.name || "",
        email: cachedUser.user.email || "",
        phoneNumber: cachedUser.user.phoneNumber || "",
      });
      setLoading(false);
      return;
    }

    // Cache'de yoksa API'den çek
    fetch(`${API_BASE_URL}/me`, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem("authToken")}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.user) {
          const userData = data;
          setUser(userData);
          cacheUserData(userData); // Session storage'a kaydet
          setForm({
            name: userData.user.name || "",
            email: userData.user.email || "",
            phoneNumber: userData.user.phoneNumber || "",
          });
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Kullanıcı verisi alınamadı:", err);
        setErrorMsg("Kullanıcı verisi alınamadı.");
        setLoading(false);
      });
  }, []);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handlePwChange = (field) => (event) => {
    setPwForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };
  
  const handlePwSubmit = (e) => {
    e.preventDefault();
  
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwError("Yeni şifreler eşleşmiyor.");
      return;
    }
  
    if (!(pwCriteria.length && pwCriteria.upper && pwCriteria.lower && pwCriteria.digit && pwCriteria.special)) {
      setPwError("Şifre; 8-16 karakter, en az bir büyük harf, bir küçük harf, bir rakam ve bir özel karakter içermelidir.");
      return;
    }
  
    setPwLoading(true);
    setPwError("");
  
    fetch(`${API_BASE_URL}/auth/change-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
      },
      body: JSON.stringify({
        oldPassword: pwForm.oldPassword,
        newPassword: pwForm.newPassword,
      }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          setPwSuccess("Şifre başarıyla güncellendi.");
          setPwForm({
            oldPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
        } else {
          setPwError(data.message || data.error || "Şifre değiştirilemedi.");
        }
      })
      .catch((err) => {
        setPwError(err?.message || "Bir hata oluştu.");
      })
      .finally(() => {
        setPwLoading(false);
      });
  };

  const handleSave = (e) => {
    e.preventDefault();
    setSaving(true);
    
    fetch(`${API_BASE_URL}/me`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
      },
      body: JSON.stringify({
        name: form.name,
        phoneNumber: form.phoneNumber,
      }),
    })
      .then(async (res) => {
        if (res.ok) {
          // API'den yanıt beklemek yerine direkt olarak local state'i güncelle
          updateUserData({
            name: form.name,
            phoneNumber: form.phoneNumber
          });
          setSuccessMsg("Profil bilgileri başarıyla güncellendi.");
        } else {
          const data = await res.json();
          throw new Error(data.error || "Güncelleme başarısız.");
        }
      })
      .catch((err) => {
        setErrorMsg(
          typeof err.message === "object" 
            ? "Güncelleme sırasında bir hata oluştu." 
            : err.message
        );
      })
      .finally(() => {
        setSaving(false);
      });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox my={3} display="flex" justifyContent="center">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <MDBox mt={2} mb={4}>
        <Grid container justifyContent="center" spacing={3}>
          <Grid item xs={12} lg={10}>
            <ConfigCard sx={{ overflow: "visible", boxShadow: 3 }}>
              <MDBox p={{ xs: 2, sm: 4 }}>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <MDTypography variant="h5" fontWeight="medium" mb={2} color="info">
                      Kullanıcı Bilgileri
                    </MDTypography>
                    <form onSubmit={handleSave}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Ad Soyad"
                            value={form.name}
                            onChange={handleChange("name")}
                            placeholder="Adınız"
                            sx={{ mb: 3 }}
                            autoComplete="name"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Telefon Numarası"
                            value={form.phoneNumber}
                            onChange={handleChange("phoneNumber")}
                            placeholder="+90 555 555 55 55"
                            sx={{ mb: 3 }}
                            autoComplete="tel"
                            type="tel"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="E-posta"
                            value={form.email}
                            placeholder="eposta@adres.com"
                            disabled
                            sx={{
                              mb: 3,
                              bgcolor: "#f5f5f5",
                              "& .MuiInputBase-root": {
                                cursor: "not-allowed",
                              },
                            }}
                            InputProps={{
                              readOnly: true,
                            }}
                            autoComplete="email"
                            type="email"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <BlueButton fullWidth type="submit" disabled={saving}>
                            {saving ? "Kaydediliyor..." : "Kaydet"}
                          </BlueButton>
                        </Grid>
                      </Grid>
                    </form>
                  </Grid>
                  <Grid item xs={12}>
                    <MDTypography variant="h5" fontWeight="medium" mb={2} color="info">
                      Şifre Değiştirme
                    </MDTypography>
                    <form onSubmit={handlePwSubmit}>
                      <Grid container spacing={2} alignItems="stretch">
                        <Grid item xs={12} sm={6}>
                          <MDBox display="flex" flexDirection="column" height="100%">
                            <TextField
                              fullWidth
                              label="Eski Şifre"
                              type={showOldPw ? "text" : "password"}
                              value={pwForm.oldPassword}
                              onChange={handlePwChange("oldPassword")}
                              placeholder="Mevcut şifrenizi girin"
                              sx={{ mb: 3 }}
                              required
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label={showOldPw ? "Şifreyi gizle" : "Şifreyi göster"}
                                      onClick={() => setShowOldPw((v) => !v)}
                                      onMouseDown={(e) => e.preventDefault()}
                                      edge="end"
                                    >
                                      {showOldPw ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                            <TextField
                              fullWidth
                              label="Yeni Şifre"
                              type={showNewPw ? "text" : "password"}
                              value={pwForm.newPassword}
                              onChange={handlePwChange("newPassword")}
                              placeholder="Yeni şifrenizi girin"
                              sx={{ mb: 1.5 }}
                              required
                              FormHelperTextProps={{ sx: { mt: 0.5, fontSize: "0.75rem" } }}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label={showNewPw ? "Şifreyi gizle" : "Şifreyi göster"}
                                      onClick={() => setShowNewPw((v) => !v)}
                                      onMouseDown={(e) => e.preventDefault()}
                                      edge="end"
                                    >
                                      {showNewPw ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                            <TextField
                              fullWidth
                              label="Yeni Şifre Tekrar"
                              type={showConfirmPw ? "text" : "password"}
                              value={pwForm.confirmPassword}
                              onChange={handlePwChange("confirmPassword")}
                              placeholder="Yeni şifrenizi tekrar girin"
                              sx={{ mb: 3 }}
                              required
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label={showConfirmPw ? "Şifreyi gizle" : "Şifreyi göster"}
                                      onClick={() => setShowConfirmPw((v) => !v)}
                                      onMouseDown={(e) => e.preventDefault()}
                                      edge="end"
                                    >
                                      {showConfirmPw ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                            <Box mt="auto" display="flex" gap={2}>
                              <BlueButton fullWidth type="submit" disabled={pwLoading}>
                                {pwLoading ? "İşleniyor..." : "Şifreyi Değiştir"}
                              </BlueButton>
                            </Box>
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <MDBox display="flex" flexDirection="column" height="100%">
                            <MDTypography variant="body2" color="text" sx={{ display: "block", mb: 0.7 }}>
                              Yeni şifreniz aşağıdaki koşulları sağlamalıdır:
                            </MDTypography>
                            <MDBox component="ul" sx={{ pl: 2, m: 0 }}>
                              <MDTypography
                                component="li"
                                variant="body2"
                                sx={{ mb: 0.3, color: (theme) => (!hasInput ? theme.palette.text.secondary : (pwCriteria.length ? theme.palette.success.main : theme.palette.error.main)) }}
                              >
                                8–16 karakter
                              </MDTypography>
                              <MDTypography
                                component="li"
                                variant="body2"
                                sx={{ mb: 0.3, color: (theme) => (!hasInput ? theme.palette.text.secondary : (pwCriteria.upper ? theme.palette.success.main : theme.palette.error.main)) }}
                              >
                                En az bir büyük harf
                              </MDTypography>
                              <MDTypography
                                component="li"
                                variant="body2"
                                sx={{ mb: 0.3, color: (theme) => (!hasInput ? theme.palette.text.secondary : (pwCriteria.lower ? theme.palette.success.main : theme.palette.error.main)) }}
                              >
                                En az bir küçük harf
                              </MDTypography>
                              <MDTypography
                                component="li"
                                variant="body2"
                                sx={{ mb: 0.3, color: (theme) => (!hasInput ? theme.palette.text.secondary : (pwCriteria.digit ? theme.palette.success.main : theme.palette.error.main)) }}
                              >
                                En az bir rakam
                              </MDTypography>
                              <MDTypography
                                component="li"
                                variant="body2"
                                sx={{ mb: 0.3, color: (theme) => (!hasInput ? theme.palette.text.secondary : (pwCriteria.special ? theme.palette.success.main : theme.palette.error.main)) }}
                              >
                                En az bir özel karakter
                              </MDTypography>
                            </MDBox>
                            <Box sx={{ mt: { xs: 5, sm: "auto" } }}>
                              <RedButton fullWidth type="button" color="error" onClick={handleDeleteClick}>
                                Hesabı Sil
                              </RedButton>
                            </Box>
                          </MDBox>
                        </Grid>
                      </Grid>
                    </form>
                  </Grid>
                </Grid>
              </MDBox>
            </ConfigCard>
          </Grid>
        </Grid>
        <Dialog open={deleteOpen} onClose={() => !deleteLoading && setDeleteOpen(false)}>
          <DialogTitle>Hesabı Sil</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              Hesabınıza ait tüm veriler kalıcı olarak silinecektir. Bu işlem geri alınamaz.
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Silmek istediğinize emin misiniz?
            </Typography>
          </DialogContent>
          <DialogActions>
            <BlueButton onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>
              Hayır
            </BlueButton>
            <RedButton
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={deleteLoading}
            >
              {deleteLoading ? "İşleniyor..." : "Evet, Sil"}
            </RedButton>
          </DialogActions>
        </Dialog>
        <Dialog open={deleteInfoOpen} onClose={() => setDeleteInfoOpen(false)}>
          <DialogTitle>Onay Gönderildi</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Güvenlik nedeniyle hesabınızı silmek için e‑postanıza bir onay bağlantısı gönderilmiştir.
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
              Lütfen e‑postanızı kontrol edip işlemi tamamlayın.
            </Typography>
          </DialogContent>
          <DialogActions>
            <BlueButton onClick={logoutAndRedirect}>
              Tamam
            </BlueButton>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={!!successMsg}
          autoHideDuration={3500}
          onClose={() => setSuccessMsg("")}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSuccessMsg("")} severity="success" sx={{ width: "100%" }}>
            {successMsg}
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!errorMsg}
          autoHideDuration={3500}
          onClose={() => setErrorMsg("")}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setErrorMsg("")} severity="error" sx={{ width: "100%" }}>
            {errorMsg}
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!pwSuccess}
          autoHideDuration={3500}
          onClose={() => setPwSuccess("")}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={() => setPwSuccess("")} severity="success" sx={{ width: "100%" }}>
            {pwSuccess}
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!pwError}
          autoHideDuration={3500}
          onClose={() => setPwError("")}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={() => setPwError("")} severity="error" sx={{ width: "100%" }}>
            {pwError}
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!deleteSuccess}
          autoHideDuration={3500}
          onClose={() => setDeleteSuccess("")}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={() => setDeleteSuccess("")} severity="success" sx={{ width: "100%" }}>
            {deleteSuccess}
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!deleteError}
          autoHideDuration={3500}
          onClose={() => setDeleteError("")}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={() => setDeleteError("")} severity="error" sx={{ width: "100%" }}>
            {deleteError}
          </Alert>
        </Snackbar>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default AccountSettings;